import { KalturaHouseholdDevice } from '@kaltura-ott/ott-client-types';
import {
  HouseholdDevice,
  householdAddDevice,
  HouseholdDeviceGetErrorCodeEnum,
  householdGetDeviceListWithStreams,
  HouseholdDeviceRemoveErrorCodeEnum,
  TIMEOUT_ERROR,
} from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import { RemoveHouseholdDeviceResponse } from '../../types/stores/device-store-type';
import { errorHandling, NETWORK_ERROR } from '../../utils/error-handling';
import { AppParametersStore } from '../app/app-params-store';
import { ErrorPriority } from '../error';
import { ToastStatus, ToastStore } from '../toast';

import type { LoginStore } from './login-store';
import { addBrandToDevices, getHouseholdDevice, removeHouseholdDevice } from './user-services';

@singleton()
export class DeviceStore {
  static moduleName = 'DeviceStore';

  constructor() {}

  @observable householdList: HouseholdDevice[] = [];
  @observable householdDevice?: KalturaHouseholdDevice;
  @observable deviceLimitExceeded = false;
  @observable householdTotalCount = 0;
  @observable totalStreamingDevices = 0;

  setHouseholdDevice(device: KalturaHouseholdDevice) {
    this.householdDevice = device as KalturaHouseholdDevice;
  }

  /**
   * Get new list of devices of the current household and save it to `householdList` property
   *  @returns Promise void | KalturaHouseholdDevice
   */
  @action.bound
  @errorHandling({ type: ErrorPriority.high })
  async addDevice(): Promise<void | KalturaHouseholdDevice> {
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
    const loginStore = container.resolve<LoginStore>('loginStore');

    try {
      if (!appParametersStore) {
        return;
      }

      const device = await lastValueFrom(householdAddDevice(appParametersStore.currentDevice));
      this.setHouseholdDevice(device as KalturaHouseholdDevice);

      this.deviceLimitExceeded = false;
      return device;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.code === '1' || error.code === HouseholdDeviceGetErrorCodeEnum.DeviceTypeNotAllowed) {
        await loginStore?.logoutUser();
        throw error;
      }

      if (error.code === HouseholdDeviceGetErrorCodeEnum.ExceededLimit) {
        this.deviceLimitExceeded = true;
        return this.getDeviceList();
      }
      throw error;
    }
  }

  /**
   * Get new list of devices of the current household and save it to `householdList` property
   *  @returns Promise<void>
   */
  @action.bound
  @errorHandling()
  public async getDeviceList(): Promise<void> {
    const deviceList = await lastValueFrom(householdGetDeviceListWithStreams());
    this.householdTotalCount = deviceList.totalCount;
    this.totalStreamingDevices = deviceList.objects.reduce((sum, device) => (device.isStreaming ? sum + 1 : sum), 0);
    this.householdList = await addBrandToDevices(deviceList?.objects);
  }

  private async removeDevice(udid: string) {
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
    const loginStore = container.resolve<LoginStore>('loginStore');

    const isCurrentDevice = udid === appParametersStore?.currentDevice.udid;

    if (isCurrentDevice) {
      return loginStore?.logoutUser();
    }

    const result = await removeHouseholdDevice(udid);

    if (result) {
      this.householdList = this.householdList.filter((device: HouseholdDevice) => device.udid !== udid);
    }
  }

  /**
   *  Remove device from household by UDID and trying to add the current device to household
   * @param udid: string
   * @param addCurrentDevice: boolean
   *
   * @returns Promise RemoveHouseholdDeviceResponse
   */
  @action.bound
  @errorHandling()
  public async removeHouseholdDevice(udid: string, addCurrentDevice?: boolean): Promise<RemoveHouseholdDeviceResponse> {
    const toastStore = container.resolve(ToastStore);
    const removeDeviceLabel = 'removeHouseholdDevice';

    try {
      await this.removeDevice(udid);

      if (addCurrentDevice) {
        await this.addDevice();
      }

      toastStore.addToast(removeDeviceLabel, ToastStatus.success);

      this.deviceLimitExceeded = false;

      return {
        success: true,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const { code } = error;
      if (code === HouseholdDeviceRemoveErrorCodeEnum.LimitationPeriod) {
        return {
          error,
          success: false,
        };
      }

      toastStore.addToast(removeDeviceLabel, ToastStatus.fail);

      throw error;
    }
  }

  /**
   *  Get current household device and set it to `householdDevice` property
   *
   * @returns Promise void
   */
  async getUserHousehold(): Promise<void> {
    const loginStore = container.resolve<LoginStore>('loginStore');

    try {
      this.setHouseholdDevice(await getHouseholdDevice());
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.code === NETWORK_ERROR || error?.code === TIMEOUT_ERROR) {
        throw error;
      }
      const deviceErrorCode: HouseholdDeviceGetErrorCodeEnum = error?.code;
      switch (deviceErrorCode) {
        case HouseholdDeviceGetErrorCodeEnum.DeviceNotExists:
        case HouseholdDeviceGetErrorCodeEnum.DeviceNotInDomain:
          await this.addDevice();
          return;
        case HouseholdDeviceGetErrorCodeEnum.ExpiredKs: // kaltura client needs to update ks
        case HouseholdDeviceGetErrorCodeEnum.DeviceRemoved:
        default:
          await loginStore?.logoutUser();
          throw error;
      }
    }
  }

  /**
   *  resetStore to the default state
   * @returns void
   */
  @action.bound
  resetStore() {
    this.householdList = [];
    this.householdDevice = undefined;
    this.deviceLimitExceeded = false;
    this.householdTotalCount = 0;
  }
}
